// lib/diagnostico/payload.ts
// Validación de las tres rutas públicas y el contrato con GHL. Puro.
import { z } from "zod"
import type { Answers, Gap } from "@/lib/diagnostico/questions"
import type { Database } from "@/lib/supabase/types"

const answerEnum = z.enum(["yes", "no", "unsure"])

export function normalizeUsPhone(raw: unknown): string | null {
  if (typeof raw !== "string") return null
  let d = raw.replace(/\D/g, "")
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1)
  if (d.length !== 10) return null
  // NANP: área y exchange no empiezan en 0 ni 1; 900 es premium.
  if (!/^[2-9][0-9]{2}[2-9][0-9]{6}$/.test(d)) return null
  if (d.startsWith("900")) return null
  return `+1${d}`
}

export function maskPhone(e164: string): string {
  const d = e164.replace(/\D/g, "").slice(-10)
  return `+1 (•••) •••-${d.slice(6)}`
}

const phoneField = z.string().transform((v, ctx) => {
  const n = normalizeUsPhone(v)
  if (!n) ctx.addIssue({ code: "custom", message: "phone" })
  return n as string
})

const emailField = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .refine((v) => v === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), { message: "email" })
  .transform((v) => (v === "" ? undefined : v))
  .optional()

export const answersSchema = z.object({ q1: answerEnum, q2: answerEnum, q3: answerEnum, q4: answerEnum, q5: answerEnum })

export const submitSchema = z.object({
  submission_id: z.string().uuid(),
  answers: answersSchema,
  name: z.string().trim().min(2).max(80),
  business: z.string().trim().min(2).max(120),
  phone: phoneField,
  email: emailField,
  city: z.string().trim().max(80).transform((v) => (v === "" ? undefined : v)).optional(),
  consent_marketing: z.boolean(),
  lang: z.enum(["es", "en"]).default("es"),
  /** Honeypot: un humano no lo ve; la ruta responde 200 silencioso si trae valor. */
  website: z.string().max(500).optional(),
})
export type SubmitInput = z.infer<typeof submitSchema>

export const verifySchema = z.object({ submission_id: z.string().uuid(), code: z.string().regex(/^\d{6}$/) })
export type VerifyInput = z.infer<typeof verifySchema>

export const resendSchema = z.object({ submission_id: z.string().uuid() })
export type ResendInput = z.infer<typeof resendSchema>

export type DeliveryStatus = "pending" | "sent" | "failed" | "skipped"

/** Las columnas de diagnostic_responses tal como las leen rutas y cron. */
export type DiagnosticRow = {
  id: string
  submission_id: string
  code: string
  status: "unverified" | "verified"
  lang: "es" | "en"
  answers: Answers
  score: number
  gaps: Gap[]
  contact_name: string
  business_name: string
  contact_phone: string
  contact_email: string | null
  city: string | null
  consent_marketing: boolean
  disclosure_version: string
  phone_code_hash: string | null
  phone_code_channel: "verify" | "ghl" | null
  phone_code_expires_at: string | null
  phone_attempts: number
  phone_sent_at: string[]
  last_code_sent_at: string | null
  /** Códigos enviados en total por esta fila (tope de vida: PHONE_MAX_SENDS_TOTAL). */
  phone_send_count: number
  phone_verified_at: string | null
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  utm_content: string | null
  utm_term: string | null
  gclid: string | null
  gbraid: string | null
  wbraid: string | null
  fbclid: string | null
  referrer_host: string | null
  landing_path: string | null
  touched_at: string | null
  account_id: string | null
  matched_merchant_id: string | null
  /** Null = el chequeo de cliente nunca corrió: GHL y Meta esperan. */
  client_checked_at: string | null
  ghl_status: DeliveryStatus
  ghl_attempts: number
  ghl_last_error: string | null
  ghl_sent_at: string | null
  meta_status: DeliveryStatus
  meta_attempts: number
  meta_last_error: string | null
  meta_sent_at: string | null
  ip: string | null
  user_agent: string | null
  created_at: string
  verified_at: string | null
}

/** Fila generada de diagnostic_responses y su parche de UPDATE (lo que PostgREST tipa). */
export type DiagnosticDbRow = Database["public"]["Tables"]["diagnostic_responses"]["Row"]
export type DiagnosticDbUpdate = Database["public"]["Tables"]["diagnostic_responses"]["Update"]

/**
 * Frontera BD → app. El esquema generado tipa `answers` como Json, `gaps` como string[]
 * y los enums de texto (status, lang, *_status, phone_code_channel) como string; los
 * CHECK de la tabla y los escritores de /api/diagnostico garantizan la forma estrecha
 * de DiagnosticRow. Es la ÚNICA conversión: nadie más castea filas a mano.
 */
export function rowFromDb(row: DiagnosticDbRow): DiagnosticRow {
  return row as unknown as DiagnosticRow
}

export function rowsFromDb(rows: readonly DiagnosticDbRow[] | null | undefined): DiagnosticRow[] {
  return (rows ?? []).map(rowFromDb)
}

/** Contrato con el workflow «Inbound Webhook → diagnostico» de GHL (spec 4.5). */
export function ghlPayload(row: DiagnosticRow, appUrl: string): Record<string, unknown> {
  return {
    event: "diagnostico",
    response_id: row.id,
    code: row.code,
    lang: row.lang,
    contact: { name: row.contact_name, business: row.business_name, phone: row.contact_phone, email: row.contact_email, city: row.city },
    consent_marketing: row.consent_marketing,
    disclosure_version: row.disclosure_version,
    consented_at: row.verified_at,
    score: row.score,
    answers: row.answers,
    gaps: row.gaps,
    attribution: {
      utm_source: row.utm_source, utm_medium: row.utm_medium, utm_campaign: row.utm_campaign,
      utm_content: row.utm_content, utm_term: row.utm_term,
      gclid: row.gclid, gbraid: row.gbraid, wbraid: row.wbraid, fbclid: row.fbclid,
    },
    crm_url: row.account_id ? `${appUrl}/superadmin/clientes/${row.account_id}` : null,
  }
}

/** Lo que la página recibe tras verificar: nunca teléfono ni correo. */
export function publicResult(row: DiagnosticRow): { score: number; gaps: Gap[]; code: string } {
  return { score: row.score, gaps: row.gaps, code: row.code }
}
